// Learning section (#735) helpers shared by the Learning page and its manage view.
//
// The catalog and the videos come from the hub (LEARNING_HUB_URL, '' when the
// hub is this deployment). The catalog is read with a plain fetch, never the
// axios client: no Authorization / X-OpCo header, so the cross-origin request
// is a "simple" one with no preflight, and no token is ever sent to the hub.
import { LEARNING_HUB_URL } from '../config/runtimeConfig'

export function hubUrl(path, hub = LEARNING_HUB_URL) {
  if (!path) return ''
  if (/^https?:\/\//i.test(path)) return path
  return `${hub || ''}${path.startsWith('/') ? '' : '/'}${path}`
}

// The video URL with a media fragment (#t=<seconds>) so the browser seeks
// there once metadata loads and paints that frame: the video's opening screen
// instead of a black box. Goes after any ?query (video_url carries ?v=).
export const OPENING_FRAME_SECONDS = 0.5

export function withOpeningFrame(url, seconds = OPENING_FRAME_SECONDS) {
  if (!url) return ''
  return `${url.split('#')[0]}#t=${seconds}`
}

export async function fetchLearningCatalog({ hub = LEARNING_HUB_URL, signal } = {}) {
  const res = await fetch(hubUrl('/api/learning/catalog', hub), {
    method: 'GET',
    credentials: 'omit',
    signal,
  })
  if (!res.ok) throw new Error(`Learning catalog unavailable (${res.status})`)
  const data = await res.json()
  return Array.isArray(data?.categories) ? data.categories : []
}

// The hub already leaves out archived items, items without a video and
// categories with nothing to watch; dropping empty categories here as well
// keeps the page safe whatever the hub sends.
export function visibleCatalog(categories) {
  return (categories || [])
    .map((category) => ({ ...category, items: category.items || [] }))
    .filter((category) => category.items.length > 0)
}

export function findItem(categories, itemId) {
  const id = Number(itemId)
  for (const category of categories || []) {
    const item = (category.items || []).find((i) => i.id === id)
    if (item) return { item, category }
  }
  return null
}

// Full screen for a <video>: the standard API, then the WebKit variants
// (iOS Safari only supports fullscreen on the video element itself).
export function enterFullscreen(el) {
  if (!el) return false
  const request = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen
  if (request) {
    const result = request.call(el)
    if (result && typeof result.catch === 'function') result.catch(() => {})
    return true
  }
  if (typeof el.webkitEnterFullscreen === 'function') {
    el.webkitEnterFullscreen()
    return true
  }
  return false
}

export function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return ''
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export const VIDEO_ACCEPT = '.mp4,.webm,.mov,video/mp4,video/webm,video/quicktime'
export const MAX_VIDEO_BYTES = 250 * 1024 * 1024

export function videoFileProblem(file) {
  if (!file) return null
  const name = (file.name || '').toLowerCase()
  if (!/\.(mp4|webm|mov)$/.test(name)) return 'Choose an .mp4, .webm or .mov video'
  if (file.size > MAX_VIDEO_BYTES) return 'The video is larger than 250 MB'
  return null
}
